import React, { useRef } from "react";
import { Braces, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ConflictRuleCatalog, RuleCondition } from "@/models/ConflictRule";
import { conditionRowPointer, RuleDiagnostics } from "@/lib/conflictRuleDiagnostics";
import RuleDiagnosticList from "./RuleDiagnosticList";

/** The server's limit on conditions per section. */
export const MAX_CONDITIONS_PER_SECTION = 10;

interface RuleConditionListProps {
  section: "/winner/when" | "/otherCopies/onlyIf";
  conditions: RuleCondition[];
  onChange: (conditions: RuleCondition[]) => void;
  catalog: ConflictRuleCatalog;
  diagnostics: RuleDiagnostics;
  /** Whether the rule applies to cases alone; placeholders such as the case number work only then. */
  casesOnly: boolean;
  addLabel: string;
  disabled?: boolean;
}

/**
 * The conditions of one section of a rule, all of which a copy must match: attribute, the folder name when the
 * attribute reads a folder, operator and value. The menus come from the server's catalog, so a new attribute or
 * operator needs no change here.
 */
const RuleConditionList: React.FC<RuleConditionListProps> = ({
  section,
  conditions,
  onChange,
  catalog,
  diagnostics,
  casesOnly,
  addLabel,
  disabled,
}) => {
  const update = (index: number, change: Partial<RuleCondition>) =>
    onChange(conditions.map((condition, i) => (i === index ? { ...condition, ...change } : condition)));

  const valueInputs = useRef(new Map<number, HTMLInputElement>());
  // Opening the placeholder menu takes focus from the value box, so its caret is kept from when it lost focus. A box
  // never focused takes the placeholder at its end.
  const carets = useRef(new Map<number, { start: number; end: number }>());
  const caretAfterInsert = useRef<{ index: number; position: number } | null>(null);

  const insertPlaceholder = (index: number, placeholder: string) => {
    const value = conditions[index].value ?? "";
    const caret = carets.current.get(index);
    const start = Math.min(caret?.start ?? value.length, value.length);
    const end = Math.min(Math.max(caret?.end ?? value.length, start), value.length);
    update(index, { value: value.slice(0, start) + placeholder + value.slice(end) });
    const position = start + placeholder.length;
    carets.current.set(index, { start: position, end: position });
    caretAfterInsert.current = { index, position };
  };

  const remove = (index: number) => {
    // The rows after it move up, so a kept caret would land in the wrong box.
    carets.current.clear();
    onChange(conditions.filter((_, i) => i !== index));
  };

  const setAttribute = (index: number, key: string) => {
    // A folder name is refused on an attribute that takes none, so it goes when the attribute changes to one.
    const takesFolder = catalog.attributes.find((attribute) => attribute.key === key)?.takesFolder ?? false;
    const { folder, ...rest } = conditions[index];
    onChange(
      conditions.map((condition, i) =>
        i === index ? { ...rest, attribute: key, ...(takesFolder ? { folder: folder ?? "" } : {}) } : condition
      )
    );
  };

  const add = () =>
    onChange([
      ...conditions,
      { attribute: catalog.attributes[0]?.key ?? "", operator: catalog.operators[0]?.key ?? "", value: null },
    ]);

  return (
    <div className="flex flex-col gap-2">
      {conditions.map((condition, index) => {
        const at = conditionRowPointer(section, index);
        const attribute = catalog.attributes.find((candidate) => candidate.key === condition.attribute);
        return (
          <div key={index} className="flex flex-col gap-1 rounded-md border bg-muted/30 p-2">
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={attribute ? condition.attribute : ""}
                onValueChange={(key) => setAttribute(index, key)}
                disabled={disabled}
              >
                <SelectTrigger
                  size="sm"
                  className="w-40"
                  aria-label={`Attribute, condition ${index + 1}`}
                  aria-invalid={diagnostics.hasErrorAt(`${at}/attribute`)}
                >
                  <SelectValue placeholder={condition.attribute || "Attribute"} />
                </SelectTrigger>
                <SelectContent>
                  {catalog.attributes.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(attribute?.takesFolder || (condition.folder ?? "") !== "") && (
                <Input
                  value={condition.folder ?? ""}
                  onChange={(event) => update(index, { folder: event.target.value })}
                  placeholder="Folder name"
                  className="h-8 w-32"
                  disabled={disabled}
                  aria-label="Folder name"
                  aria-invalid={diagnostics.hasErrorAt(`${at}/folder`)}
                />
              )}
              <Select
                value={catalog.operators.some((op) => op.key === condition.operator) ? condition.operator : ""}
                onValueChange={(key) => update(index, { operator: key })}
                disabled={disabled}
              >
                <SelectTrigger
                  size="sm"
                  className="w-40"
                  aria-label={`Operator, condition ${index + 1}`}
                  aria-invalid={diagnostics.hasErrorAt(`${at}/operator`)}
                >
                  <SelectValue placeholder={condition.operator || "Operator"} />
                </SelectTrigger>
                <SelectContent>
                  {catalog.operators.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                ref={(input) => {
                  if (input) valueInputs.current.set(index, input);
                  else valueInputs.current.delete(index);
                }}
                value={condition.value ?? ""}
                // An empty box is no value, which the server asks for. Kept as "" it would be a real value: "is" empty
                // matches every copy without that part, such as a drive-letter path for Server.
                onChange={(event) => update(index, { value: event.target.value === "" ? null : event.target.value })}
                onBlur={(event) =>
                  carets.current.set(index, {
                    start: event.currentTarget.selectionStart ?? (condition.value ?? "").length,
                    end: event.currentTarget.selectionEnd ?? (condition.value ?? "").length,
                  })
                }
                placeholder="Value"
                className="h-8 min-w-40 flex-1 font-mono text-xs"
                disabled={disabled}
                aria-label="Value"
                aria-invalid={diagnostics.hasErrorAt(`${at}/value`)}
              />
              {catalog.placeholders.length > 0 && (
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" disabled={disabled} aria-label="Insert a placeholder">
                          <Braces className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Insert a placeholder</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent
                    align="end"
                    onCloseAutoFocus={(event) => {
                      const pending = caretAfterInsert.current;
                      if (pending?.index !== index) return;
                      // Back to the value, just after the placeholder, rather than to the menu button.
                      event.preventDefault();
                      caretAfterInsert.current = null;
                      requestAnimationFrame(() => {
                        const input = valueInputs.current.get(index);
                        input?.focus();
                        input?.setSelectionRange(pending.position, pending.position);
                      });
                    }}
                  >
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {casesOnly ? "Filled in per group" : "Only in a rule on cases alone"}
                    </DropdownMenuLabel>
                    {catalog.placeholders.map((placeholder) => (
                      <DropdownMenuItem
                        key={placeholder.key}
                        disabled={!casesOnly}
                        onSelect={() => insertPlaceholder(index, placeholder.key)}
                      >
                        <span className="font-mono text-xs">{placeholder.key}</span>
                        <span className="text-xs text-muted-foreground">{placeholder.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => remove(index)}
                    disabled={disabled}
                    aria-label="Remove condition"
                  >
                    <X className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove condition</TooltipContent>
              </Tooltip>
            </div>
            <RuleDiagnosticList diagnostics={diagnostics.at(at)} />
          </div>
        );
      })}
      <Button
        variant="outline"
        size="sm"
        className="w-fit gap-1"
        onClick={add}
        disabled={disabled || conditions.length >= MAX_CONDITIONS_PER_SECTION || catalog.attributes.length === 0}
      >
        <Plus className="size-4" />
        {addLabel}
      </Button>
    </div>
  );
};

export default RuleConditionList;
